import { ReportType, Language } from "@/domain/records/types";

/**
 * Domain Model for Setup Configuration
 * Clean Architecture - Domain Layer
 */
export class SetupConfig {
  constructor(
    public readonly recordId: string,
    public readonly goals: string, // Research Context
    public readonly notes: string, // Additional Info
    public readonly guide: string, // Interview Guide
    public readonly reportTypes: ReportType[],
    public readonly language?: Language, // Optional: only include when explicitly updating language
    public readonly participantCount?: number,
    public readonly respondentNames: string[] = [],
    public readonly isWithTranscript: boolean = true,
    public readonly isWithInsightReport: boolean = true,
    public readonly customPrompt?: string,
    public readonly sttProvider?: "auto" | "assembly" | "soniox"
  ) {}

  /**
   * Check if custom report type is selected
   */
  get hasCustomReport(): boolean {
    return this.reportTypes.includes("Custom");
  }

  /**
   * Check if custom prompt is required and valid
   */
  get isCustomPromptValid(): boolean {
    if (!this.hasCustomReport) return true;
    return !!(
      this.customPrompt && this.customPrompt.trim().length > 0
    );
  }

  /**
   * Get report types count
   */
  get reportTypesCount(): number {
    return this.reportTypes.length;
  }

  /**
   * Check if configuration is valid for generation
   */
  get isValidForGeneration(): boolean {
    return (
      this.reportTypes.length > 0 &&
      this.isCustomPromptValid &&
      this.goals.trim().length > 0
    );
  }

  /**
   * Create a copy with updated properties
   */
  with(
    updates: Partial<
      Pick<
        SetupConfig,
        | "goals"
        | "notes"
        | "guide"
        | "reportTypes"
        | "language"
        | "participantCount"
        | "respondentNames"
        | "isWithTranscript"
        | "isWithInsightReport"
        | "customPrompt"
        | "sttProvider"
      >
    >
  ): SetupConfig {
    return new SetupConfig(
      this.recordId,
      updates.goals ?? this.goals,
      updates.notes ?? this.notes,
      updates.guide ?? this.guide,
      updates.reportTypes ?? this.reportTypes,
      updates.language ?? this.language,
      updates.participantCount ?? this.participantCount,
      updates.respondentNames ?? this.respondentNames,
      updates.isWithTranscript ?? this.isWithTranscript,
      updates.isWithInsightReport ?? this.isWithInsightReport,
      updates.customPrompt ?? this.customPrompt,
      updates.sttProvider ?? this.sttProvider
    );
  }
}

export type SetupConfigUpdatableField =
  | "goals"
  | "notes"
  | "guide"
  | "reportTypes"
  | "language"
  | "participantCount"
  | "respondentNames"
  | "isWithTranscript"
  | "isWithInsightReport"
  | "customPrompt"
  | "sttProvider";
